import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, buildFileUrl, formatEuro, formatKm, whatsappLink } from "@/api";
import { MessageCircle, Calendar as Cal, X, ChevronLeft, ChevronRight, Calculator } from "lucide-react";
import { toast } from "sonner";

const RateSimulator = ({ prezzo }) => {
  const [anticipo, setAnticipo] = useState(Math.round(prezzo * 0.2));
  const [durata, setDurata] = useState(60);
  const tan = 0.0699; // 6.99% annuale indicativo
  const capitale = Math.max(prezzo - anticipo, 0);
  const rate = durata > 0 && capitale > 0
    ? (capitale * (tan / 12)) / (1 - Math.pow(1 + tan / 12, -durata))
    : 0;
  return (
    <div className="bg-[#0B0B0C] border border-[#E1BC56]/25 rounded-lg p-5 mt-5" data-testid="rate-simulator">
      <div className="flex items-center gap-2 mb-4">
        <Calculator className="w-4 h-4 text-[#E1BC56]" />
        <div className="font-display text-xs font-bold tracking-widest text-[#E1BC56]">SIMULATORE RATE</div>
      </div>
      <div className="space-y-4 text-sm">
        <div>
          <div className="flex justify-between text-xs text-zinc-400 mb-1"><span className="tracking-widest uppercase">Anticipo</span><span className="text-white font-bold">{formatEuro(anticipo)}</span></div>
          <input type="range" min="0" max={prezzo} step="500" value={anticipo} onChange={(e) => setAnticipo(parseInt(e.target.value))} className="w-full accent-[#E1BC56]" data-testid="rate-anticipo" />
        </div>
        <div>
          <div className="flex justify-between text-xs text-zinc-400 mb-1"><span className="tracking-widest uppercase">Durata</span><span className="text-white font-bold">{durata} mesi</span></div>
          <input type="range" min="12" max="120" step="12" value={durata} onChange={(e) => setDurata(parseInt(e.target.value))} className="w-full accent-[#E1BC56]" data-testid="rate-durata" />
        </div>
        <div className="border-t border-white/10 pt-4 flex items-baseline justify-between">
          <span className="text-xs tracking-widest uppercase text-zinc-400">Rata mensile stimata</span>
          <span className="font-display text-2xl font-extrabold gold-text tracking-wider" data-testid="rate-rata">{formatEuro(rate)}</span>
        </div>
        <p className="text-[10px] text-zinc-500 leading-relaxed">Stima indicativa (TAN 6,99%). Preventivo reale su richiesta con partner finanziari LC Auto.</p>
      </div>
    </div>
  );
};

const Spec = ({ label, value }) => (
  <div className="border-b border-white/5 py-2.5 flex justify-between text-sm">
    <span className="text-zinc-500 tracking-wider uppercase text-xs">{label}</span>
    <span className="text-white font-medium">{value || "—"}</span>
  </div>
);

const VehicleDetail = () => {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [idx, setIdx] = useState(0);
  const [showTD, setShowTD] = useState(false);
  const [td, setTd] = useState({ nome: "", email: "", telefono: "", data_preferita: "", messaggio: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get(`/vehicles/${id}`).then((r) => setV(r.data)); }, [id]);

  if (!v) return <div className="max-w-7xl mx-auto px-6 py-20 text-zinc-500">Caricamento…</div>;

  const imgs = (v.galleria || []).map(buildFileUrl);
  const submitTD = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/testdrive", { vehicle_id: v.id, ...td });
      toast.success("Richiesta inviata! Ti contatteremo presto.");
      setShowTD(false);
    } catch { toast.error("Errore, riprova"); }
    setSending(false);
  };

  const badgeClass = v.badge?.toLowerCase().includes("arrivo") ? "badge-in-arrivo" : v.badge?.toLowerCase().includes("vendut") ? "badge-venduta" : "badge-disponibile";

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <Link to="/parco-veicoli" className="text-xs text-[#E1BC56] tracking-widest font-display hover:text-[#F3E29F]" data-testid="back-to-catalog">← TORNA AL CATALOGO</Link>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-6">
        {/* Gallery */}
        <div className="lg:col-span-7">
          <div className="relative aspect-video bg-[#121214] rounded-lg overflow-hidden border border-white/10" data-testid="vehicle-gallery">
            {imgs[idx] && <img src={imgs[idx]} alt={v.titolo} className="w-full h-full object-cover" />}
            <div className={`absolute top-4 left-4 px-3 py-1.5 rounded-sm text-xs font-bold tracking-widest uppercase ${badgeClass}`}>{v.badge}</div>
            {imgs.length > 1 && (
              <>
                <button onClick={() => setIdx((idx - 1 + imgs.length) % imgs.length)} className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 border border-white/10 w-10 h-10 rounded-full flex items-center justify-center hover:border-[#E1BC56]"><ChevronLeft className="w-5 h-5" /></button>
                <button onClick={() => setIdx((idx + 1) % imgs.length)} className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/60 border border-white/10 w-10 h-10 rounded-full flex items-center justify-center hover:border-[#E1BC56]"><ChevronRight className="w-5 h-5" /></button>
              </>
            )}
          </div>
          {imgs.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-3">
              {imgs.map((im, i) => (
                <button key={im} onClick={() => setIdx(i)} className={`aspect-video rounded-sm overflow-hidden border ${i === idx ? "border-[#E1BC56]" : "border-white/10"}`}>
                  <img src={im} alt={`gallery ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          {v.video_url && (
            <div className="mt-6 aspect-video bg-[#121214] rounded-lg overflow-hidden border border-white/10">
              <video src={buildFileUrl(v.video_url)} controls className="w-full h-full" />
            </div>
          )}
          <div className="mt-8">
            <h2 className="font-display text-xl font-bold tracking-widest text-white mb-3">DESCRIZIONE</h2>
            <p className="text-zinc-300 leading-relaxed whitespace-pre-line">{v.descrizione}</p>
          </div>
          {v.optionals?.length > 0 && (
            <div className="mt-8">
              <h2 className="font-display text-xl font-bold tracking-widest text-white mb-3">OPTIONAL & DOTAZIONI</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {v.optionals.map((o) => (
                  <div key={o} className="text-sm text-zinc-300 flex items-center gap-2"><span className="text-[#E1BC56]">◆</span>{o}</div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Specs & CTA */}
        <div className="lg:col-span-5">
          <div className="bg-[#121214] border border-white/10 rounded-lg p-6 sticky top-24">
            <div className="text-xs text-zinc-500 tracking-widest uppercase mb-1">{v.marca}</div>
            <h1 className="font-display text-2xl md:text-3xl font-extrabold text-white tracking-wider uppercase mb-4" data-testid="vehicle-title">{v.titolo}</h1>
            <div className="text-3xl font-display font-bold gold-text tracking-wider mb-6" data-testid="vehicle-price">{formatEuro(v.prezzo)}</div>
            <div className="space-y-0 mb-6">
              <Spec label="Anno" value={v.anno} />
              <Spec label="Chilometraggio" value={formatKm(v.km)} />
              <Spec label="Alimentazione" value={v.alimentazione} />
              <Spec label="Cambio" value={v.cambio} />
              <Spec label="Cilindrata" value={v.cilindrata ? `${v.cilindrata} cc` : null} />
              <Spec label="Potenza" value={v.potenza} />
              <Spec label="Colore Esterno" value={v.colore_esterno} />
              <Spec label="Colore Interno" value={v.colore_interno} />
            </div>
            <div className="space-y-3">
              <a href={whatsappLink(`Ciao LC Auto, vorrei informazioni sulla ${v.titolo}`)} target="_blank" rel="noreferrer" className="cmc-btn-gold rounded-sm py-3.5 flex items-center justify-center gap-2 w-full" data-testid="detail-whatsapp-btn">
                <MessageCircle className="w-4 h-4" /> RICHIEDI INFO SU WHATSAPP
              </a>
              <button onClick={() => setShowTD(true)} className="cmc-btn-outline rounded-sm py-3.5 flex items-center justify-center gap-2 w-full" data-testid="detail-testdrive-btn">
                <Cal className="w-4 h-4" /> PRENOTA TEST DRIVE
              </button>
            </div>
            <RateSimulator prezzo={v.prezzo} />
          </div>
        </div>
      </div>

      {showTD && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowTD(false)}>
          <div className="bg-[#121214] border border-[#E1BC56]/30 rounded-lg max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="testdrive-modal">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="text-xs tracking-widest text-[#E1BC56] font-display mb-1">TEST DRIVE</div>
                <h3 className="font-display text-xl font-bold text-white tracking-wider">{v.titolo}</h3>
              </div>
              <button onClick={() => setShowTD(false)} className="text-zinc-500 hover:text-white"><X /></button>
            </div>
            <form onSubmit={submitTD} className="space-y-3">
              <input required placeholder="Nome e cognome" value={td.nome} onChange={(e) => setTd({ ...td, nome: e.target.value })} className="w-full bg-[#0B0B0C] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-white focus:border-[#E1BC56] focus:outline-none" data-testid="td-nome" />
              <input required type="email" placeholder="Email" value={td.email} onChange={(e) => setTd({ ...td, email: e.target.value })} className="w-full bg-[#0B0B0C] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-white focus:border-[#E1BC56] focus:outline-none" data-testid="td-email" />
              <input required placeholder="Telefono" value={td.telefono} onChange={(e) => setTd({ ...td, telefono: e.target.value })} className="w-full bg-[#0B0B0C] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-white focus:border-[#E1BC56] focus:outline-none" data-testid="td-telefono" />
              <input type="date" value={td.data_preferita} onChange={(e) => setTd({ ...td, data_preferita: e.target.value })} className="w-full bg-[#0B0B0C] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-white focus:border-[#E1BC56] focus:outline-none" data-testid="td-data" />
              <textarea placeholder="Messaggio (opzionale)" rows={3} value={td.messaggio} onChange={(e) => setTd({ ...td, messaggio: e.target.value })} className="w-full bg-[#0B0B0C] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-white focus:border-[#E1BC56] focus:outline-none" data-testid="td-messaggio" />
              <button disabled={sending} type="submit" className="cmc-btn-gold w-full py-3 rounded-sm" data-testid="td-submit">{sending ? "INVIO..." : "PRENOTA ORA"}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleDetail;
